import utils from 'utils/utils';
import { Model } from 'utils/model';
import type { Category, CategoryImages } from '@schibsted-svp/svp-api-types';

type ParsedCategory = Omit<Category, 'additional'> &
    Omit<
        Exclude<Category['additional'], null>,
        'background' | 'image' | 'logo' | 'playerLogoImage'
    > & { images: CategoryImages };

export class CategoryModel extends Model<ParsedCategory> {
    constructor(options?: Partial<Category>) {
        super({ id: 0 });

        this.initialize(options);
    }

    initialize(data?: Partial<Category>) {
        utils.extend(this.attributes, this.parse(data));
    }

    parse(data: Partial<Category & ParsedCategory> = {}) {
        if (data.additional) {
            const imgKeys = [
                'background',
                'image',
                'logo',
                'playerLogoImage',
            ] as const;
            data.images = imgKeys.reduce(
                (images, key) =>
                    Object.assign(images, {
                        [key]: data.additional![key],
                    }),
                (data.images || {}) as ParsedCategory['images'],
            );

            utils.each(
                data.additional,
                <K extends keyof ParsedCategory>(
                    value: ParsedCategory[K],
                    key: K,
                ) => {
                    if (
                        typeof data[key] === 'undefined' &&
                        !imgKeys.includes(key as (typeof imgKeys)[number])
                    ) {
                        data[key] = value;
                    }
                },
            );

            delete data.additional;
        }

        return data as ParsedCategory;
    }

    getId(): number {
        return this.get('id');
    }

    getTitle(): string {
        return this.get('title');
    }

    getAcastPrependUrl(): string | undefined {
        return this.get('metadata')?.podcast_acast_prependUrl?.value;
    }

    getMetadata(): Record<string, { value: string; inherited: boolean }> {
        return this.get('metadata');
    }
}
